/**
 * Day view — single-column time grid matching the week view style. Extracted
 * verbatim (behavior-preserving) from EventListModule's `renderDayView`. Every
 * value the original render function read from component scope is now an
 * explicit, fully-typed prop. The component calls no hooks of its own, so the
 * new boundary produces identical output.
 *
 * getEventBg / getEventDotColor are pure lib helpers imported directly from
 * ../lib/eventColors rather than threaded through props.
 */
import React, { useState } from 'react';
import { AgentShimmer } from '@/common/components/AgentShimmer';
import { objectDragSource } from '@/common/dnd/objectDragSource';
import { layoutOverlappingEvents } from '../event-layout';
import { parseEventDate, isAllDayish } from '../dateTimeUtils';
import { getEventBg, getEventDotColor } from '../lib/eventColors';
import { useCalendar24Hour, formatHourLabel, formatEventTime } from '../lib/timeFormat';
import { MobileLongPressEvent } from '../components/MobileLongPressEvent';
import { computeHourHeight } from '../hourHeightComputation';
import { DAY_GRID_START_HOUR, DAY_GRID_END_HOUR } from '../calendarConstants';
import type { EventObject } from '../eventTypes';

export interface DayViewProps {
  calendarDate: Date;
  isMobile: boolean;
  isExpandedView: boolean;
  dayWeekBodyHeight: number;
  dayWeekContainerRef: React.RefObject<HTMLDivElement | null>;
  timeGridRef: React.RefObject<HTMLDivElement | null>;
  /** #3952: ids flagged as same-slot suspected duplicates → mark the chip. */
  suspectedDuplicateIds: Set<string>;
  getEventsForDate: (date: Date) => EventObject[];
  isToday: (date: Date) => boolean;
  handleEventClick: (event: EventObject) => void;
  handleCalendarSlotClick: (date: Date, hour: number) => void;
  onCompleteTaskEvent: (event: EventObject) => void;
  onOpenTaskEvent: (event: EventObject) => void;
}

export function DayView({
  calendarDate,
  isMobile,
  isExpandedView,
  dayWeekBodyHeight,
  dayWeekContainerRef,
  timeGridRef,
  suspectedDuplicateIds,
  getEventsForDate,
  isToday,
  handleEventClick,
  handleCalendarSlotClick,
  onCompleteTaskEvent,
  onOpenTaskEvent,
}: DayViewProps) {
  const dayEvents = getEventsForDate(calendarDate);
  const allDayEvents = dayEvents.filter(e => isAllDayish(e));
  const timedEvents = dayEvents.filter(e => !isAllDayish(e));
  const isTodayDate = isToday(calendarDate);

  // Cap the all-day band so a busy day doesn't push the time grid down; the
  // overflow collapses behind a "+N more" toggle (Google-calendar style).
  const MAX_ALLDAY = 3;
  const [showAllAllDay, setShowAllAllDay] = useState(false);
  const visibleAllDayEvents = showAllAllDay ? allDayEvents : allDayEvents.slice(0, MAX_ALLDAY);
  const hiddenAllDayCount = allDayEvents.length - visibleAllDayEvents.length;

  // #3534/#3585: grid bounds live in calendarConstants so the views and the
  // scroll-to-8 AM target (useCalendarNavigation) share one source of truth.
  const START_HOUR = DAY_GRID_START_HOUR;
  const END_HOUR = DAY_GRID_END_HOUR;
  const visibleHours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
  // #3619: scale per-hour height to fill the measured container instead of a
  // fixed value, so a tall window has no blank gap below the last hour. The
  // expanded base is the zoomed 96px, so the day stays zoomed-in and scrollable
  // on normal-height windows.
  const HOUR_HEIGHT = computeHourHeight(isMobile, isExpandedView, dayWeekBodyHeight, visibleHours.length);
  // No extra space below the last hour — the grid ends exactly at midnight.
  const GRID_BOTTOM_PADDING = 0;
  // End-of-day boundary (midnight), used to clamp event heights and the gutter
  // vertical line so neither overruns the grid.
  const dayEndPx = visibleHours.length * HOUR_HEIGHT;
  const gridHeight = dayEndPx + GRID_BOTTOM_PADDING;

  const [use24Hour] = useCalendar24Hour();

  // Google-style top-of-day label: the GMT offset shown where 00:00 would be,
  // so the first hour label is 01:00 and there's no "12 AM" at the very top.
  const tzLabel = (() => {
    const offMin = new Date().getTimezoneOffset();
    const sign = offMin > 0 ? '-' : '+';
    const abs = Math.abs(offMin);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = abs % 60;
    return `GMT${sign}${hh}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`;
  })();

  const now = new Date();
  const nowTop = ((now.getHours() - START_HOUR) + now.getMinutes() / 60) * HOUR_HEIGHT;
  // Clamp to half the dot height (4px) so translateY(-50%) never produces a
  // negative top value that gets clipped by the column's overflow-x:hidden.
  const nowTopSafe = Math.max(4, nowTop);
  const showNowLine = now.getHours() >= START_HOUR && now.getHours() < END_HOUR;

  // #1541: Prefer measured container height (fills viewport); fall back to the
  // legacy `12 * HOUR_HEIGHT` only on the very first paint before the
  // ResizeObserver has populated dayWeekBodyHeight.
  const bodyHeight = dayWeekBodyHeight > 0 ? dayWeekBodyHeight : 12 * HOUR_HEIGHT;

  return (
    <div className="xv-week" ref={dayWeekContainerRef}>
      {/* All-day events */}
      {allDayEvents.length > 0 && (
        <div className="xv-week-allday" style={{ gridTemplateColumns: '44px 1fr' }}>
          <div className="xv-week-header-gutter" />
          <div className="xv-week-allday-col">
            {visibleAllDayEvents.map(event => {
              const allDayAccentColor = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
              const allDayStyle = { position: 'relative' as const, overflow: 'hidden', boxSizing: 'border-box' as const, background: getEventBg(event.eventType, true, event.metadata?.customColor), borderLeft: `3px solid ${allDayAccentColor}` };
              // #3952: same-slot suspected-duplicate marker on all-day chips.
              const allDayDupClass = suspectedDuplicateIds.has(event.id) ? ' gcal-event--dup' : '';
              // #3951: outbound-push failure marker on all-day chips.
              const allDaySyncErrorClass = event.providerSyncState === 'error' ? ' gcal-event--sync-error' : '';
              const allDaySyncErrorTitle = event.providerSyncState === 'error' ? ' — not synced to Google (push failed; open to retry)' : '';
              return isMobile ? (
                <MobileLongPressEvent
                  key={event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id}
                  className={`gcal-event gcal-event-allday${allDayDupClass}${allDaySyncErrorClass}`}
                  style={allDayStyle}
                  onLongPress={() => (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event)}
                >
                  {event._sourceTaskId && (
                    <button
                      type="button"
                      className="xv-task-event-check"
                      aria-label={`Complete ${event.title}`}
                      title="Mark complete"
                      onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </button>
                  )}
                  <AgentShimmer objectId={event.id} />
                  <span className="gcal-event-title">{event.title}</span>
                </MobileLongPressEvent>
              ) : (
                <div
                  key={event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id}
                  className={`gcal-event gcal-event-allday${allDayDupClass}${allDaySyncErrorClass}`}
                  style={allDayStyle}
                  onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); } }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${event.title}${allDaySyncErrorTitle}`}
                  title={`${event.title}${allDaySyncErrorTitle}`}
                  // Drag this event into the chat composer to @-reference it (#1457).
                  {...objectDragSource({ id: event.id, type: 'event', title: event.title })}
                >
                  {event._sourceTaskId && (
                    <button
                      type="button"
                      className="xv-task-event-check"
                      aria-label={`Complete ${event.title}`}
                      title="Mark complete"
                      onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </button>
                  )}
                  <AgentShimmer objectId={event.id} />
                  <span className="gcal-event-title">{event.title}</span>
                </div>
              );
            })}
            {(hiddenAllDayCount > 0 || showAllAllDay) && (
              <button
                type="button"
                className="xv-allday-more"
                onClick={(e) => { e.stopPropagation(); setShowAllAllDay(v => !v); }}
              >
                {showAllAllDay ? 'Show less' : `+${hiddenAllDayCount} more`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scrollable time grid — single column */}
      <div ref={timeGridRef} className="xv-week-body" style={{ height: bodyHeight, flex: 'none', gridTemplateColumns: '44px 1fr' }}>
        {/* Time gutter. The default border-right spans the full height; disable
            it and draw the vertical line as an element that stops at midnight so
            the scroll-past buffer below has no trailing line. */}
        <div className="xv-time-gutter" style={{ height: gridHeight, borderRight: 'none' }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '1px', height: gridHeight, background: '#ececef' }} />
          {visibleHours.map((h, i) => (
            <div
              key={h}
              className="xv-time-label"
              // Center each hour label on its hour line. The top (GMT) label
              // stays top-aligned so it isn't clipped at the grid's top edge.
              style={
                i === 0
                  ? { top: i * HOUR_HEIGHT }
                  : { top: i * HOUR_HEIGHT, transform: 'translateY(-50%)', paddingTop: 0 }
              }
            >
              {i === 0 ? tzLabel : formatHourLabel(h, use24Hour)}
            </div>
          ))}
        </div>

        {/* Single day column */}
        <div className="xv-day-column" style={{ height: gridHeight, flex: 1 }}>
          {/* Hour lines */}
          {visibleHours.map((h, i) => (
            <React.Fragment key={h}>
              <div className="xv-hour-line" style={{ top: i * HOUR_HEIGHT }} />
              <div className="xv-half-hour-line" style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
            </React.Fragment>
          ))}

          {/* Click targets */}
          {visibleHours.map((h, i) => (
            <div
              key={`slot-${h}`}
              style={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, height: HOUR_HEIGHT, cursor: 'pointer' }}
              onClick={() => handleCalendarSlotClick(calendarDate, h)}
            />
          ))}

          {/* Current time indicator */}
          {isTodayDate && showNowLine && (
            <>
              <div className="xv-now-dot" style={{ top: nowTopSafe }} />
              <div className="xv-now-line" style={{ top: nowTopSafe }} />
            </>
          )}

          {/* Events — overlapping events laid out side-by-side (#1473) */}
          {layoutOverlappingEvents(timedEvents).map(({ event, lane, columns }) => {
            const startTime = parseEventDate(event.startAt) ?? new Date();
            const endTime = event.endAt ? (parseEventDate(event.endAt) ?? startTime) : new Date(startTime.getTime() + 60 * 60 * 1000);
            const durationMin = Math.max((endTime.getTime() - startTime.getTime()) / 60000, 0);
            const top = ((startTime.getHours() - START_HOUR) + startTime.getMinutes() / 60) * HOUR_HEIGHT;
            // Clamp the box to the end of the day so an event that runs past
            // midnight doesn't overflow below the grid and inflate the scroll
            // height (which made the bottom overscroll bounce get stuck).
            // Task-derived events are a zero-duration instant; give them a taller
            // floor (vs the 18px event minimum) so the single-row title + time
            // has padding above and below and stays readable.
            const minHeight = event._sourceTaskId ? 30 : 18;
            const height = Math.min(Math.max(durationMin / 60 * HOUR_HEIGHT, minHeight), Math.max(0, dayEndPx - top));
            // #1473: width/left in % so day-view collapses N concurrent events
            // into N side-by-side sub-columns. 1.5% gap between events for
            // visual separation; clipped to lane==0 left edge so it still
            // hugs the column wall.
            const widthPct = 100 / columns;
            const leftPct = lane * widthPct;
            const gapPct = columns > 1 ? 1.5 : 0;
            // Events extend a few px PAST the hour lines (which stop at 13px),
            // but still clear the scrollbar — so a smaller right inset than the
            // lines.
            const RIGHT_INSET = 6;
            // Task-derived events are a single specific moment (zero duration) —
            // show one time, not a start–end range.
            const timeRange = event._sourceTaskId
              ? formatEventTime(startTime, use24Hour)
              : `${formatEventTime(startTime, use24Hour)} - ${formatEventTime(endTime, use24Hour)}`;
            // Short bubbles can't fit the stacked title + time line without
            // clipping. Below ~2 lines we switch to a single row: title (ellipsis)
            // with the time range pinned to the right so neither gets cut off.
            const compact = height < 34;
            const eventKey = event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id;
            // #3952: same-slot suspected-duplicate marker (left-edge ring/dot via
            // the .xv-week-event--dup CSS); click still opens the modal to resolve.
            const isDup = suspectedDuplicateIds.has(event.id);
            const dupClass = isDup ? ' xv-week-event--dup' : '';
            const dupTitle = isDup ? ' — possible duplicate (same time slot, similar title)' : '';
            // #3951: outbound-push failure marker — a left-edge ring + ⚠ glyph
            // (CSS .xv-week-event--sync-error). Click still opens the modal,
            // where the "not synced to Google" banner offers Retry.
            const isSyncError = event.providerSyncState === 'error';
            const syncErrorClass = isSyncError ? ' xv-week-event--sync-error' : '';
            const syncErrorTitle = isSyncError ? ' — not synced to Google (push failed; open to retry)' : '';
            const sourceAccentColor = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
            const laneStyle: React.CSSProperties = {
              top,
              height,
              // Override the CSS `left: 2px; right: 3px` rule so the inline
              // width takes effect — #1473.
              left: `calc((100% - ${RIGHT_INSET}px) * ${leftPct / 100} + ${lane > 0 ? gapPct / 2 : 0}px)`,
              right: 'auto',
              width: `calc((100% - ${RIGHT_INSET}px) * ${widthPct / 100} - ${gapPct}px)`,
              overflow: 'hidden',
              boxSizing: 'border-box',
              background: getEventBg(event.eventType, false, event.metadata?.customColor),
              // Source indicator: left border keyed off calendar color (Google) or
              // slate (#64748b) for Xavior-native events. Makes source visible at a
              // glance without a separate badge. Closes #2836.
              borderLeft: `3px solid ${sourceAccentColor}`,
            };

            const sourceLabel = event.providerCalendarId ? (event.providerCalendarName || 'Calendar') : 'Xavior';
            const ariaLabel = `${event.title}, ${timeRange}, ${sourceLabel}`;

            return isMobile ? (
              <MobileLongPressEvent
                key={eventKey}
                eventId={event.id}
                className={`xv-week-event${compact ? ' xv-week-event--compact' : ''}${event._sourceTaskId ? ' xv-week-event--task' : ''}${dupClass}${syncErrorClass}`}
                style={laneStyle}
                aria-label={`${ariaLabel}${syncErrorTitle}`}
                onLongPress={() => (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event)}
              >
                {event._sourceTaskId && (
                  <button
                    type="button"
                    className="xv-task-event-check"
                    aria-label={`Complete ${event.title}`}
                    title="Mark complete"
                    onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </button>
                )}
                <AgentShimmer objectId={event.id} />
                <span className="xv-week-event-label">{event.title}</span>
                <span className="xv-week-event-time">{timeRange}</span>
              </MobileLongPressEvent>
            ) : (
              <div
                key={eventKey}
                data-event-id={event.id}
                className={`xv-week-event${compact ? ' xv-week-event--compact' : ''}${event._sourceTaskId ? ' xv-week-event--task' : ''}${dupClass}${syncErrorClass}`}
                style={laneStyle}
                role="button"
                tabIndex={0}
                aria-label={`${ariaLabel}${dupTitle}${syncErrorTitle}`}
                onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                title={`${event.title}${dupTitle}${syncErrorTitle}`}
              >
                {event._sourceTaskId && (
                  <button
                    type="button"
                    className="xv-task-event-check"
                    aria-label={`Complete ${event.title}`}
                    title="Mark complete"
                    onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </button>
                )}
                <AgentShimmer objectId={event.id} />
                <span className="xv-week-event-label">{event.title}</span>
                <span className="xv-week-event-time">{timeRange}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
